import React, { useEffect, useState, useRef } from 'react';
import { Box } from 'grommet';
import LiveCard from './LiveCard';
import NoGamesToDisplay from './NoGamesToDisplay';
import IScoreSyncButton from './iScoreSyncButton';
import SHDHeading from '@ui/Heading/SHDHeading';
import { UpcomingCardListProps, ScheduleStatus } from './schedule.d';
import { TeamGameResponse } from '@dto/api';
import { useLiveOnPlatformV2 } from '@features/SidelineLive/liveHooks';
import {
  pollIntervals,
  CameraStatus,
} from '@features/SidelineLive/sidelineLive.types';

const UpcomingCardList: React.FC<UpcomingCardListProps> = ({
  scheduleStatus,
  todayGames,
  upcomingGames,
  pastGames,
  teamId,
  handlePropUpdates = () => ({}),
  upcomingListRef,
  todayListRef,
  pastListRef,
  viewportSize,
  isAdmin,
  isIScore,
  onClick,
}) => {
  const hlsRef = useRef<string | undefined | null>(undefined);
  const hasTodayGamesToDisplay = todayGames && todayGames.length > 0;
  const hasUpcomingGamesToDisplay = upcomingGames && upcomingGames.length > 0;
  const hasPastGamesToDisplay = pastGames && pastGames.length > 0;

  const displayIScoreSyncButton = isIScore && isAdmin;

  const { livestreamClient, setPolling } = useLiveOnPlatformV2(teamId);
  const [hasLink, setHasLink] = useState(false);
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>(
    CameraStatus.Down
  );
  const [isPlayable, setIsPlayable] = useState<boolean>(false);
  const [gameIsStreaming, setGameIsStreaming] = useState<boolean>(false);

  useEffect(() => {
    setCameraStatus(livestreamClient?.camera_status as CameraStatus);
    setIsPlayable(livestreamClient?.isPlayable || false);

    if (livestreamClient?.isPlayable && !hasLink) {
      hlsRef.current = livestreamClient?.hls_link;
      setHasLink(true);
      setPolling(() => pollIntervals.SIXTY_SECONDS);
    }
    if (
      livestreamClient?.camera_status !== CameraStatus.Live &&
      livestreamClient?.isPlayable
    ) {
      setPolling(() => pollIntervals.THIRTY_SECONDS);
      setHasLink(false);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livestreamClient?.isPlayable, livestreamClient?.camera_status, hasLink]);

  useEffect(() => {
    setGameIsStreaming(isPlayable && cameraStatus === CameraStatus.Live);
  }, [isPlayable, cameraStatus]);

  const renderLiveCard = (
    game: TeamGameResponse,
    listStatus: ScheduleStatus
  ) => {
    return (
      <LiveCard
        hlsRef={hlsRef}
        key={game.id}
        teamId={teamId}
        gameInfo={game}
        onClick={onClick}
        viewportSize={viewportSize}
        cameraStatus={cameraStatus}
        isPlayable={isPlayable}
        gameIsStreaming={gameIsStreaming}
        scheduleStatus={listStatus}
      />
    );
  };

  return (
    <Box>
      {hasPastGamesToDisplay && (
        <Box
          id={`schedule-${ScheduleStatus.PAST.toLowerCase()}-list`}
          data-testid={`schedule-${ScheduleStatus.PAST.toLowerCase()}-list`}
          fill="vertical"
          ref={pastListRef}
        >
          <SHDHeading
            level={5}
            color="black"
            margin={{ top: 'small', bottom: 'xxsmall', left: 'xxxsmall' }}
            formatCasing={false}
          >
            {ScheduleStatus.PAST}
          </SHDHeading>
          <Box {...handlePropUpdates()}>
            {pastGames?.map((game: TeamGameResponse) => {
              return renderLiveCard(game, ScheduleStatus.PAST);
            })}
          </Box>
        </Box>
      )}
      {hasTodayGamesToDisplay && (
        <Box
          id={`schedule-${ScheduleStatus.TODAY.toLowerCase()}-list`}
          data-testid={`schedule-${ScheduleStatus.TODAY.toLowerCase()}-list`}
          fill="vertical"
          ref={todayListRef}
        >
          <SHDHeading
            level={5}
            color="black"
            margin={{ top: 'small', bottom: 'xxsmall', left: 'xxxsmall' }}
            formatCasing={false}
          >
            {ScheduleStatus.TODAY}
          </SHDHeading>
          <Box {...handlePropUpdates()}>
            {todayGames?.map((game: TeamGameResponse) => {
              return renderLiveCard(game, ScheduleStatus.TODAY);
            })}
          </Box>
        </Box>
      )}
      <Box
        id={`schedule-${ScheduleStatus.UPCOMING.toLowerCase()}-list`}
        data-testid={`schedule-${ScheduleStatus.UPCOMING.toLowerCase()}-list`}
        fill="vertical"
        ref={upcomingListRef}
      >
        {hasUpcomingGamesToDisplay && !isIScore && (
          <Box direction="row" justify="between">
            <SHDHeading
              level={5}
              color="black"
              margin={{ top: 'small', bottom: 'xxsmall', left: 'xxxsmall' }}
              formatCasing={false}
            >
              {ScheduleStatus.UPCOMING}
            </SHDHeading>
          </Box>
        )}
        {isIScore && (
          <Box direction="row" justify="between">
            <SHDHeading
              level={5}
              color="black"
              margin={{ top: 'small', bottom: 'xxsmall', left: 'xxxsmall' }}
              formatCasing={false}
            >
              {isIScore ? ScheduleStatus.ISCORE : ScheduleStatus.UPCOMING}
            </SHDHeading>
            {displayIScoreSyncButton && <IScoreSyncButton teamId={teamId} />}
          </Box>
        )}
        <Box {...handlePropUpdates()}>
          {upcomingGames?.map((game: TeamGameResponse) => {
            return renderLiveCard(
              game,
              isIScore ? ScheduleStatus.ISCORE : ScheduleStatus.UPCOMING
            );
          })}
          {!hasUpcomingGamesToDisplay ? (
            <NoGamesToDisplay
              isIScore={scheduleStatus === ScheduleStatus.ISCORE}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default UpcomingCardList;
